import { motion } from "framer-motion";
import {
  ArrowRight, Zap, BarChart3, CreditCard,
  Bell, Shield, CheckCircle2, Sparkles, Target,
  TrendingUp, Award, Play, Mail,
  ShoppingBag, Package, Users,
  Heart, CheckCircle, XCircle, Settings,
  Repeat, Layers
} from "lucide-react";

/* ─── Design tokens — Retail warm teal system ─── */
const TEAL       = "rgb(93,162,162)";
const TEAL_DARK  = "rgb(58,120,120)";
const TEAL_LIGHT = "rgb(145,205,205)";
const INK        = "#18110a";
const INK2       = "#3a2d20";
const MUTED      = "#806550";
const BG         = "#fffaf5";
const WHITE      = "#ffffff";
const BORDER     = "#ecddd0";
const DARK_BG    = "#16100a";
const DARK_BG2   = "#0e0a06";

/* ─── Shared Badge ─── */
const Badge = ({ icon: Icon, label }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.10)", border: "1px solid rgba(93,162,162,0.25)", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
    {Icon && <Icon style={{ width: 14, height: 14, color: TEAL }} />}
    <span style={{ color: TEAL, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{label}</span>
  </div>
);

/* ══════════════════════════
   HERO
══════════════════════════ */
const Hero = () => (
  <section style={{ background: DARK_BG2, padding: "100px 40px 80px", position: "relative", overflow: "hidden", minHeight: "92vh", display: "flex", alignItems: "center" }}>
    {/* Warm amber glow top-right */}
    <div style={{ position: "absolute", top: "-10%", right: "-5%", width: "55%", height: "70%", background: "radial-gradient(ellipse, rgba(210,140,50,0.13) 0%, transparent 65%)", pointerEvents: "none" }} />
    {/* Teal glow bottom-left */}
    <div style={{ position: "absolute", bottom: "-5%", left: "-3%", width: "40%", height: "55%", background: "radial-gradient(ellipse, rgba(93,162,162,0.08) 0%, transparent 65%)", pointerEvents: "none" }} />
    {/* Subtle grid */}
    <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(93,162,162,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(93,162,162,0.04) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />

    <div style={{ maxWidth: 1280, margin: "0 auto", position: "relative", width: "100%" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>

        {/* Left */}
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.12)", border: "1px solid rgba(93,162,162,0.30)", padding: "7px 16px", borderRadius: 100, marginBottom: 24 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#4ade80", display: "inline-block" }} />
            <span style={{ color: TEAL_LIGHT, fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>RETAIL & STORE MANAGEMENT PLATFORM</span>
          </div>

          <h1 style={{ fontSize: "clamp(2.4rem,4vw,3.8rem)", fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.08, color: "white", marginBottom: 24 }}>
            Run Your Retail Store<br />
            <span style={{ background: `linear-gradient(90deg, ${TEAL_LIGHT}, ${TEAL})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Smarter.</span>
          </h1>

          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.55)", lineHeight: 1.75, marginBottom: 36, maxWidth: 500 }}>
            Krumago Retail is the all-in-one management platform built for store owners. Manage sales, inventory, billing, and customer loyalty — without the complexity or cost of enterprise software.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ background: "rgba(93,162,162,0.20)", color: "rgba(255,255,255,0.92)", border: "1px solid rgba(145,205,205,0.32)", padding: "15px 36px", borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, backdropFilter: "blur(8px)" }}>
              Start Free Trial <ArrowRight style={{ width: 17, height: 17 }} />
            </motion.button>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ background: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.78)", border: "1px solid rgba(255,255,255,0.18)", padding: "15px 36px", borderRadius: 11, fontSize: 15, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}>
              <Play style={{ width: 15, height: 15 }} /> Watch Demo
            </motion.button>
          </div>

          {/* Feature pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 40, paddingTop: 32, borderTop: "1px solid rgba(255,255,255,0.08)", flexWrap: "wrap" }}>
            {[
              { icon: ShoppingBag, label: "POS & Billing" },
              { icon: Package,     label: "Inventory Control" },
              { icon: BarChart3,   label: "Sales Analytics" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(93,162,162,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon style={{ width: 14, height: 14, color: TEAL_LIGHT }} />
                </div>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Right — POS dashboard mockup */}
        <motion.div initial={{ opacity: 0, x: 36 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: 24, backdropFilter: "blur(20px)" }}>
            {/* Chrome */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 20 }}>
              {["#ef4444","#f59e0b","#22c55e"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
              <div style={{ flex: 1, marginLeft: 10, height: 24, borderRadius: 6, background: "rgba(255,255,255,0.05)" }} />
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 16 }}>
              {[["—","Today's Sales",TEAL_LIGHT],["—","Items Sold","#fbbf24"],["—%","Stock Healthy","#4ade80"]].map(([v,l,c]) => (
                <div key={l} style={{ background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "16px 14px" }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: c, marginBottom: 4 }}>{v}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>{l}</div>
                </div>
              ))}
            </div>

            {/* Inventory list */}
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", marginBottom: 12 }}>LIVE STOCK STATUS</div>
              {[
                ["Category A","In Stock",TEAL_LIGHT],
                ["Category B","Low Stock","#fbbf24"],
                ["Category C","In Stock",TEAL_LIGHT],
                ["Category D","Reorder Alert","#f87171"],
              ].map(([name,status,color]) => (
                <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }}>{name}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color, background: `${color}18`, padding: "3px 10px", borderRadius: 100 }}>{status}</span>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={{ background: TEAL, color: "white", borderRadius: 10, padding: "11px 0", textAlign: "center", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>New Sale</div>
              <div style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.6)", borderRadius: 10, padding: "11px 0", textAlign: "center", fontSize: 13, fontWeight: 700, border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer" }}>View Reports</div>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  </section>
);

/* ══════════════════════════
   MARQUEE
══════════════════════════ */
const Marquee = () => {
  const items = [
    { icon: ShoppingBag, label: "POS & Fast Checkout" },
    { icon: Package,     label: "Live Inventory Tracking" },
    { icon: CreditCard,  label: "Automated Billing & GST" },
    { icon: Users,       label: "Customer Loyalty Program" },
    { icon: BarChart3,   label: "Sales & Revenue Analytics" },
    { icon: Bell,        label: "Low Stock Alerts" },
    { icon: Repeat,      label: "Returns & Exchange Management" },
    { icon: Layers,      label: "Multi-Store Dashboard" },
  ];
  return (
    <div style={{ background: DARK_BG, borderTop: "1px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "18px 0", overflow: "hidden" }}>
      <motion.div animate={{ x: ["0%", "-50%"] }} transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        style={{ display: "flex", gap: 56, whiteSpace: "nowrap" }}>
        {[...items, ...items].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} style={{ display: "inline-flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <Icon style={{ width: 15, height: 15, color: TEAL_LIGHT }} />
              <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, fontWeight: 600 }}>{item.label}</span>
              <span style={{ color: "rgba(255,255,255,0.15)", marginLeft: 20 }}>·</span>
            </div>
          );
        })}
      </motion.div>
    </div>
  );
};

/* ══════════════════════════
   MISSION / STORY
══════════════════════════ */
const Mission = () => (
  <section style={{ background: WHITE, padding: "100px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>

        {/* Visual */}
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.65 }}>
          <div style={{ position: "relative" }}>
            <div style={{ background: `linear-gradient(135deg, #16100a, #1e180e)`, borderRadius: 28, padding: 40, boxShadow: "0 40px 80px rgba(15,10,5,0.28)", border: `1px solid rgba(93,162,162,0.12)` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: TEAL_LIGHT, letterSpacing: "0.1em", marginBottom: 24 }}>STORE SALES DASHBOARD</div>

              {/* Mini bar chart */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80, marginBottom: 20 }}>
                {[38,55,42,70,50,80,65,88,60,92,75,100].map((h, i) => (
                  <motion.div key={i} initial={{ height: 0 }} whileInView={{ height: `${h}%` }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
                    style={{ flex: 1, borderRadius: 4, background: i === 11 ? TEAL : "rgba(255,255,255,0.1)" }} />
                ))}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Daily Revenue</div>
                  <div style={{ color: "white", fontSize: 28, fontWeight: 900 }}>Your Data</div>
                  <div style={{ color: "#4ade80", fontSize: 13, fontWeight: 600 }}>↑ Growing sales</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Stock Health</div>
                  <div style={{ color: "white", fontSize: 28, fontWeight: 900 }}>Live</div>
                </div>
              </div>
            </div>

            {/* Floating billing badge */}
            <motion.div animate={{ y: [0,-8,0] }} transition={{ duration: 4, repeat: Infinity }}
              style={{ position: "absolute", bottom: -22, right: -20, background: WHITE, borderRadius: 18, padding: "16px 20px", boxShadow: "0 20px 50px rgba(0,0,0,0.10)", border: `1px solid ${BORDER}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(93,162,162,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CreditCard style={{ width: 18, height: 18, color: TEAL_DARK }} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>Invoice Auto-Generated</div>
                  <div style={{ fontSize: 12, color: MUTED }}>GST ready · Sent to customer</div>
                </div>
              </div>
            </motion.div>

            {/* Floating badge */}
            <motion.div animate={{ y: [0,7,0] }} transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              style={{ position: "absolute", top: -18, left: -16, background: TEAL, borderRadius: 16, padding: "12px 18px", boxShadow: `0 12px 36px rgba(93,162,162,0.4)` }}>
              <div style={{ color: "white", fontSize: 16, fontWeight: 900 }}>All-in-One</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11, fontWeight: 600 }}>Retail Platform</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Text */}
        <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.65 }}>
          <Badge icon={Sparkles} label="OUR STORY" />
          <h2 style={{ fontSize: "clamp(1.9rem,3vw,2.8rem)", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.12, color: INK, marginBottom: 20 }}>
            We built what store owners actually needed.
          </h2>
          <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.8, marginBottom: 18 }}>
            In 2021, our team visited hundreds of retail stores across the country. Every owner was dealing with the same headaches: manual stock counts, slow billing, no visibility into what was selling, and customer data living nowhere useful.
          </p>
          <p style={{ fontSize: 16, color: MUTED, lineHeight: 1.8, marginBottom: 32 }}>
            Existing tools were too complex, too expensive, or built for large chains — not for the independent store owner or growing retail business. So we built Krumago Retail: purpose-designed for the real challenges of running a store.
          </p>

          {[
            "Purpose-built for retail, not adapted from generic software",
            "Replaces disconnected tools with one unified platform",
            "From a single counter to a multi-store chain",
            "Stores go live in under an hour",
          ].map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <CheckCircle2 style={{ width: 18, height: 18, color: TEAL, flexShrink: 0 }} />
              <span style={{ fontSize: 15, fontWeight: 500, color: INK }}>{item}</span>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </div>
  </section>
);

/* ══════════════════════════
   PROBLEM / SOLUTION
══════════════════════════ */
const ProblemSolution = () => {
  const problems = [
    "Stock managed on paper or disconnected spreadsheets",
    "No real-time sales visibility across counters",
    "Manual billing prone to errors and delays",
    "Zero customer purchase history or loyalty tracking",
    "No alerts for low stock or expiring items",
    "Returns and exchanges handled without records",
  ];
  const solutions = [
    "Live inventory tracking with auto-reorder alerts",
    "Real-time sales dashboard across all registers",
    "Fast automated billing with GST & receipt generation",
    "Full customer CRM with purchase history & loyalty",
    "Smart alerts for low stock and expiry dates",
    "Streamlined returns & exchange management",
  ];
  const benefits = [
    { icon: ShoppingBag, title: "POS & Sales",           desc: "Fast multi-register checkout with multiple payment modes and instant receipts." },
    { icon: Package,     title: "Inventory Management",  desc: "Track every SKU in real-time. Get alerts before you run out of bestsellers." },
    { icon: BarChart3,   title: "Revenue Analytics",     desc: "Understand top products, peak hours, and profit margins at a glance." },
  ];

  return (
    <section style={{ background: BG, padding: "96px 40px", overflow: "hidden" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <Badge icon={Zap} label="THE RETAIL PROBLEM" />
          <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: INK }}>
            Built specifically for <span style={{ color: TEAL }}>Retailers</span>
          </h2>
          <p style={{ fontSize: 18, color: MUTED, maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
            Unlike generic tools, Krumago Retail is purpose-built for store owners who need sales, stock and customers in one place.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
          {/* Without */}
          <div style={{ background: WHITE, border: "1px solid #fde0e0", borderRadius: 22, padding: 40, boxShadow: "0 2px 20px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 42, height: 42, background: "#fef2f2", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <XCircle style={{ width: 20, height: 20, color: "#ef4444" }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: INK }}>Without Krumago Retail</h3>
            </div>
            {problems.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#fff6f6", borderRadius: 10, border: "1px solid #fde0e0", marginBottom: 10 }}>
                <XCircle style={{ width: 16, height: 16, color: "#fca5a5", flexShrink: 0 }} />
                <span style={{ color: "#8a6060", fontSize: 14, lineHeight: 1.5 }}>{p}</span>
              </div>
            ))}
          </div>

          {/* With */}
          <div style={{ background: WHITE, border: "1px solid rgba(93,162,162,0.22)", borderRadius: 22, padding: 40, boxShadow: "0 2px 24px rgba(93,162,162,0.07)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 42, height: 42, background: "rgba(93,162,162,0.10)", borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckCircle2 style={{ width: 20, height: 20, color: TEAL }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: INK }}>With Krumago Retail</h3>
            </div>
            {solutions.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(93,162,162,0.05)", borderRadius: 10, border: "1px solid rgba(93,162,162,0.14)", marginBottom: 10 }}>
                <CheckCircle2 style={{ width: 16, height: 16, color: TEAL, flexShrink: 0 }} />
                <span style={{ color: INK, fontSize: 14, lineHeight: 1.5, fontWeight: 500 }}>{s}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }}>
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 18, padding: "28px 26px", textAlign: "center", cursor: "default", transition: "all 0.3s", boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 48px rgba(93,162,162,0.12)"; e.currentTarget.style.borderColor = "rgba(93,162,162,0.32)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.03)"; e.currentTarget.style.borderColor = BORDER; }}>
                <div style={{ width: 52, height: 52, background: "rgba(93,162,162,0.09)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: TEAL }}>
                  <Icon style={{ width: 22, height: 22 }} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 17, color: INK, marginBottom: 8 }}>{b.title}</div>
                <div style={{ color: MUTED, fontSize: 14, lineHeight: 1.65 }}>{b.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ══════════════════════════
   FEATURES
══════════════════════════ */
const features = [
  { icon: ShoppingBag, title: "POS & Sales",            desc: "Fast barcode scanning, multi-register checkout, and multiple payment modes — all from one clean screen.", color: TEAL },
  { icon: Package,     title: "Inventory Control",       desc: "Track every SKU in real-time. Set reorder points and receive automatic alerts before stock runs out.", color: "#c07830" },
  { icon: CreditCard,  title: "Billing & Invoicing",     desc: "Auto-generate GST-compliant invoices and digital receipts. Send instantly by SMS or email.", color: "#4a9e78" },
  { icon: BarChart3,   title: "Sales Analytics",         desc: "Revenue trends, top-selling products, and margin reports to help you make smarter buying decisions.", color: TEAL },
  { icon: Users,       title: "Customer CRM & Loyalty",  desc: "Full purchase history for every customer. Run loyalty programs, offers, and targeted promotions.", color: "#c07830" },
  { icon: Bell,        title: "Smart Alerts",            desc: "Low stock, expiry warnings, and daily sales summaries delivered automatically — nothing falls through the cracks.", color: "#0891b2" },
  { icon: Repeat,      title: "Returns & Exchanges",     desc: "Handle returns and exchanges with full records and automatic stock adjustments in seconds.", color: "#4a9e78" },
  { icon: Layers,      title: "Multi-Store Management",  desc: "Manage multiple branches from one dashboard. Consolidate reports, stock, and staff across locations.", color: "#7c3aed" },
];

const Features = () => (
  <section style={{ background: WHITE, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 64 }}>
        <Badge icon={Zap} label="PLATFORM FEATURES" />
        <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: INK }}>
          Everything to run your <span style={{ color: TEAL }}>Retail Store</span>
        </h2>
        <p style={{ fontSize: 18, color: MUTED, maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
          Purpose-built tools for store owners — from a single counter to a growing multi-location chain.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }}>
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
              style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 18, padding: 28, cursor: "default", transition: "all 0.3s", position: "relative", overflow: "hidden" }}
              onMouseEnter={e => { e.currentTarget.style.background = WHITE; e.currentTarget.style.borderColor = "rgba(93,162,162,0.32)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(93,162,162,0.12)"; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = BG; e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
              <div style={{ width: 52, height: 52, background: "rgba(93,162,162,0.09)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", color: f.color, marginBottom: 18 }}>
                <Icon style={{ width: 24, height: 24 }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 16, color: INK, marginBottom: 10, lineHeight: 1.3 }}>{f.title}</div>
              <div style={{ color: MUTED, fontSize: 14, lineHeight: 1.6 }}>{f.desc}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   HOW IT WORKS
══════════════════════════ */
const steps = [
  {
    step: "01", icon: Settings, color: TEAL, bg: "rgba(93,162,162,0.09)",
    title: "Set Up Your Store",
    desc: "Add products, configure categories, set pricing and tax rules. Connect your payment modes and counters — no technical expertise needed.",
    points: ["Import products via CSV or add manually", "Configure GST and tax rules", "Set up counters & payment modes"],
  },
  {
    step: "02", icon: ShoppingBag, color: "#c07830", bg: "rgba(192,120,48,0.09)",
    title: "Start Selling",
    desc: "Take sales instantly. Scan barcodes, apply discounts, accept any payment and send digital receipts — all from one screen.",
    points: ["Barcode scanning & quick item search", "Apply discounts and loyalty points", "Digital & printed receipts on every sale"],
  },
  {
    step: "03", icon: TrendingUp, color: "#4a9e78", bg: "rgba(74,158,120,0.09)",
    title: "Grow & Automate",
    desc: "Reorder alerts, GST reports, loyalty rewards, and analytics all run automatically while you focus on your customers.",
    points: ["Auto reorder alerts & supplier orders", "GST reports & invoice automation", "Loyalty programs & customer retention"],
  },
];

const HowItWorks = () => (
  <section style={{ background: BG, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 64 }}>
        <Badge icon={TrendingUp} label="HOW IT WORKS" />
        <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", color: INK, lineHeight: 1.1 }}>
          Up and running in <span style={{ color: TEAL }}>3 simple steps</span>
        </h2>
        <p style={{ fontSize: 18, color: MUTED, maxWidth: 560, margin: "14px auto 0", lineHeight: 1.7 }}>
          From product setup to full automation — launch your retail operation on Krumago in less than a day.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 22 }}>
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
              style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 22, padding: 38, position: "relative", transition: "all 0.3s", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 22px 52px rgba(0,0,0,0.09)"; e.currentTarget.style.borderColor = "rgba(93,162,162,0.28)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.04)"; e.currentTarget.style.borderColor = BORDER; }}>
              <div style={{ position: "absolute", top: -14, left: 28, background: WHITE, border: `2px solid ${s.color}`, borderRadius: 10, padding: "5px 13px", boxShadow: `0 4px 12px ${s.color}30` }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: s.color, letterSpacing: "0.08em" }}>STEP {s.step}</span>
              </div>
              <div style={{ width: 56, height: 56, background: s.bg, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: s.color, marginBottom: 20, marginTop: 10 }}>
                <Icon style={{ width: 26, height: 26 }} />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 800, color: INK, marginBottom: 12 }}>{s.title}</h3>
              <p style={{ color: MUTED, fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>{s.desc}</p>
              {s.points.map((p, pi) => (
                <div key={pi} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
                  <CheckCircle style={{ width: 15, height: 15, color: s.color, flexShrink: 0, marginTop: 1 }} />
                  <span style={{ color: INK2, fontSize: 14, fontWeight: 500, lineHeight: 1.5 }}>{p}</span>
                </div>
              ))}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 4, background: s.color, borderRadius: "0 0 22px 22px", opacity: 0.7 }} />
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   PLATFORM PILLARS
══════════════════════════ */
const Pillars = () => {
  const pillars = [
    { icon: ShoppingBag, color: TEAL,      title: "POS & Checkout",      desc: "Fast, reliable point-of-sale with barcode scanning, discounts, and multi-payment support." },
    { icon: Package,     color: "#c07830",  title: "Inventory Control",   desc: "Real-time stock levels, auto-reorder triggers, and expiry alerts across every location." },
    { icon: CreditCard,  color: "#4a9e78",  title: "Billing Automation",  desc: "GST-compliant invoices, digital receipts, and tax reports that generate automatically." },
    { icon: BarChart3,   color: "#0891b2",  title: "Sales Intelligence",  desc: "Revenue dashboards, product performance, and margin tracking to guide your buying decisions." },
  ];
  return (
    <section style={{ background: DARK_BG, padding: "80px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24 }}>
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                style={{ textAlign: "center", padding: "40px 24px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 20 }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}22`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <Icon style={{ width: 24, height: 24, color: p.color }} />
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "white", marginBottom: 10 }}>{p.title}</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 14, lineHeight: 1.65 }}>{p.desc}</div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

 

/* ══════════════════════════
   VALUES
══════════════════════════ */
const values = [
  { icon: Target,    color: TEAL,      title: "Retailer-First",      desc: "Every feature is designed for the real day-to-day of running a store — not adapted from enterprise ERP." },
  { icon: Zap,       color: "#c07830",  title: "Speed to Value",      desc: "Set up and start selling in under an hour. No complex configuration. No IT team needed." },
  { icon: Heart,     color: "#e55c7a",  title: "Real Partnership",    desc: "We support every store owner like a partner. Your feedback shapes every update we ship." },
  { icon: Shield,    color: "#4a9e78",  title: "Secure & Reliable",   desc: "Your sales data and customer information are protected with enterprise-grade security." },
];

const Values = () => (
  <section style={{ background: BG, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 56 }}>
        <Badge icon={Award} label="OUR VALUES" />
        <h2 style={{ fontSize: 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: INK }}>
          Why store owners <span style={{ color: TEAL }}>choose us</span>
        </h2>
        <p style={{ fontSize: 18, color: MUTED, maxWidth: 500, margin: "14px auto 0", lineHeight: 1.7 }}>
          We're not just software — we genuinely care about the success of every store we work with.
        </p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20 }}>
        {values.map((v, i) => {
          const Icon = v.icon;
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 20, padding: 32, textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: `${v.color}12`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <Icon style={{ width: 26, height: 26, color: v.color }} />
              </div>
              <div style={{ fontWeight: 800, fontSize: 17, color: INK, marginBottom: 10 }}>{v.title}</div>
              <div style={{ color: MUTED, fontSize: 14, lineHeight: 1.65 }}>{v.desc}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   CTA
══════════════════════════ */
const CTA = () => (
  <section style={{ background: WHITE, padding: "96px 40px" }}>
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ background: DARK_BG, borderRadius: 28, padding: "76px 60px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle at 2px 2px, rgba(255,230,190,0.045) 1px, transparent 0)", backgroundSize: "44px 44px" }} />
        <div style={{ position: "absolute", top: "10%", left: "5%", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(200,130,50,0.09) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", bottom: "8%", right: "5%", width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(93,162,162,0.11) 0%, transparent 70%)" }} />
        <div style={{ position: "absolute", top: -80, right: -80, width: 360, height: 360, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
        <div style={{ position: "absolute", bottom: -60, left: -60, width: 260, height: 260, borderRadius: "50%", background: "rgba(255,255,255,0.02)" }} />

        <div style={{ position: "relative" }}>
          <div style={{ width: 68, height: 68, background: "rgba(93,162,162,0.14)", border: "1px solid rgba(93,162,162,0.28)", borderRadius: 20, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px" }}>
            <ShoppingBag style={{ width: 34, height: 34, color: TEAL_LIGHT }} />
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(93,162,162,0.14)", border: "1px solid rgba(93,162,162,0.25)", color: TEAL_LIGHT, padding: "7px 18px", borderRadius: 100, fontSize: 12, fontWeight: 700, marginBottom: 24, letterSpacing: "0.06em" }}>
            <Sparkles style={{ width: 13, height: 13 }} /> BUILT FOR RETAILERS
          </div>
          <h2 style={{ fontSize: 46, fontWeight: 900, color: "white", letterSpacing: "-0.025em", lineHeight: 1.08, marginBottom: 18 }}>
            The smarter way to<br />run your store
          </h2>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.50)", maxWidth: 540, margin: "0 auto 50px", lineHeight: 1.78 }}>
            Manage sales, inventory, billing and loyalty — all from one platform built for modern retail businesses.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, maxWidth: 740, margin: "0 auto 50px" }}>
            {[
              { icon: Zap,       label: "Live in Under an Hour" },
              { icon: Shield,    label: "Secure & Compliant" },
              { icon: Layers,    label: "Scales to Any Store Count" },
              { icon: BarChart3, label: "Full Sales Analytics" },
            ].map((h, i) => {
              const Icon = h.icon;
              return (
                <div key={i} style={{ background: "rgba(255,255,255,0.06)", borderRadius: 14, padding: "18px 14px", textAlign: "center", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <div style={{ color: TEAL_LIGHT, marginBottom: 9, display: "flex", justifyContent: "center" }}><Icon style={{ width: 18, height: 18 }} /></div>
                  <div style={{ color: "rgba(255,255,255,0.82)", fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}>{h.label}</div>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ padding: "16px 42px", borderRadius: 11, fontSize: 15, fontWeight: 700, background: "white", color: TEAL_DARK, border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.22)" }}>
              Get Started Free <ArrowRight style={{ width: 17, height: 17 }} />
            </motion.button>
            <motion.button whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }}
              style={{ padding: "16px 42px", borderRadius: 11, fontSize: 15, fontWeight: 700, background: "transparent", color: "rgba(255,255,255,0.82)", border: "1.5px solid rgba(93,162,162,0.38)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}>
              <Mail style={{ width: 16, height: 16 }} /> Request Demo
            </motion.button>
          </div>

          <div style={{ display: "flex", gap: 30, justifyContent: "center", marginTop: 34, flexWrap: "wrap" }}>
            {[
              { dot: "#4ade80",               text: "Start for free" },
              { dot: TEAL_LIGHT,              text: "Explore all features for 14 days" },
              { dot: "rgba(255,200,140,0.55)", text: "Setup in under an hour" },
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: t.dot }} />
                <span style={{ color: "rgba(255,255,255,0.38)", fontSize: 13 }}>{t.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ══════════════════════════
   ROOT
══════════════════════════ */
const RetailAbout = () => (
  <div>
    <Hero />
    <Marquee />
    <Mission />
    <ProblemSolution />
    <Features />
    <HowItWorks />
    <Pillars />
    {/* <Testimonials /> */}
    <Values />
    <CTA />
  </div>
);

export default RetailAbout;
